import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

type ModelsResp = {
  var: { id: string; name: string; gpu: boolean }[]
  universes: string[]
  gpu_available: boolean
  bridge: { configured: boolean; healthy?: boolean; url?: string; gpu_device?: string; error?: string }
}

type VaRRow = { model: string; horizon: number; confidence: number; var: number | null; es: number | null; method?: string }
type VaRResp = { run_id: number; n_obs: number; results: VaRRow[]; via_bridge?: boolean; bridge_runtime_ms?: number }

const ALL_HORIZONS = [1, 5, 21]
const ALL_CONFS = [0.95, 0.99]

export default function RiskVaR() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [universe, setUniverse] = useState('narrow')
  const [window, setWindow] = useState('full')
  const [horizons, setHorizons] = useState<number[]>([1, 5, 21])
  const [confs, setConfs] = useState<number[]>([0.95, 0.99])
  const [models, setModels] = useState<string[]>(['gaussian', 'historical', 'polymodel_narrow'])
  const [allowGpu, setAllowGpu] = useState(false)
  const [nSamples, setNSamples] = useState(5000)

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })
  const modelsQ = useQuery<ModelsResp>({
    queryKey: ['risk-stats-models'],
    queryFn: () => api.get('/risk-stats/models').then(r => r.data),
  })

  const run = useMutation<VaRResp, unknown>({
    mutationFn: () => api.post('/risk-stats/var', {
      fund_id: fundId,
      models, horizons, confidences: confs,
      universe, time_window: window,
      n_samples: nSamples, allow_gpu: allowGpu,
    }).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data
      .filter(f => !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s))
      .slice(0, 300)
  }, [fundsQ.data, search])

  const toggle = <T,>(arr: T[], v: T, setter: (x: T[]) => void) => {
    setter(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])
  }

  const matrix = useMemo(() => {
    // Build per-horizon/per-confidence matrix: rows = model, cols = (horizon×conf) pair
    if (!run.data?.results) return null
    const rows = new Map<string, Record<string, VaRRow>>()
    for (const r of run.data.results) {
      const key = `${r.horizon}d_${Math.round(r.confidence * 100)}`
      if (!rows.has(r.model)) rows.set(r.model, {})
      rows.get(r.model)![key] = r
    }
    return rows
  }, [run.data])

  const colKeys = horizons.flatMap(h => confs.map(c => ({ key: `${h}d_${Math.round(c * 100)}`, h, c })))

  const pct = (v: number | null | undefined) => v == null ? '—' : `${(v * 100).toFixed(2)}%`

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Value at Risk</h1>
        <p className="text-muted-foreground">Run multiple VaR engines side-by-side. Results persist with a <span className="font-mono">run_id</span> in <span className="font-mono">plutus_risk</span>.</p>
      </div>

      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        {/* Row 1: fund */}
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name or ticker..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          <select
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            className="input w-full md:w-[28rem]"
          >
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>
                {f.name} ({f.ticker}) [{f.source}]
              </option>
            ))}
          </select>
        </div>

        {/* Row 2: universe / window / n_samples / GPU */}
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Universe</span>
            <select value={universe} onChange={e => setUniverse(e.target.value)} className="input">
              <option value="narrow">narrow (BBG + LRK)</option>
              <option value="wide">wide (+ 1277 strategies)</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Window</span>
            <select value={window} onChange={e => setWindow(e.target.value)} className="input">
              <option value="full">full</option>
              <option value="10y">10y</option>
              <option value="5y">5y</option>
              <option value="2y">2y</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">n samples</span>
            <input
              type="number"
              value={nSamples}
              onChange={e => setNSamples(Math.max(100, Number(e.target.value) || 0))}
              className="input w-24"
            />
          </label>
          <label className="flex items-center gap-2 text-sm" title={modelsQ.data && !modelsQ.data.bridge.configured ? 'Deploy ft.risk.bridge on a GPU host to enable (see repo deploy/ folder).' : ''}>
            <input
              type="checkbox"
              checked={allowGpu && !!modelsQ.data?.bridge.configured}
              disabled={modelsQ.data && !modelsQ.data.bridge.configured}
              onChange={e => setAllowGpu(e.target.checked)}
            />
            <span className={modelsQ.data && !modelsQ.data.bridge.configured ? 'text-muted-foreground' : ''}>
              Allow GPU (route signature via ft.risk.bridge)
            </span>
            {modelsQ.data && !modelsQ.data.bridge.configured && (
              <span className="text-xs text-muted-foreground">· bridge not configured</span>
            )}
            {modelsQ.data?.bridge.configured && !modelsQ.data?.bridge.healthy && (
              <span className="text-xs text-destructive">· bridge unhealthy</span>
            )}
          </label>
        </div>

        {/* Row 3: horizons / confidences */}
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Horizons</span>
            {ALL_HORIZONS.map(h => (
              <label key={h} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={horizons.includes(h)}
                  onChange={() => toggle(horizons, h, setHorizons)}
                />
                <span>{h}d</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Confidences</span>
            {ALL_CONFS.map(c => (
              <label key={c} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={confs.includes(c)}
                  onChange={() => toggle(confs, c, setConfs)}
                />
                <span>{Math.round(c * 100)}%</span>
              </label>
            ))}
          </div>
        </div>

        {/* Row 4: models */}
        <div>
          <div className="text-sm text-muted-foreground mb-1">Models</div>
          <div className="flex flex-wrap gap-2">
            {modelsQ.data?.var.filter(m => m.id !== 'ensemble' && m.id !== 'cornish_fisher' && m.id !== 'dcc_garch').map(m => {
              const active = models.includes(m.id)
              const disabled = m.gpu && !allowGpu
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggle(models, m.id, setModels)}
                  className={
                    'text-xs px-2 py-1 rounded border transition-colors ' +
                    (disabled
                      ? 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed'
                      : active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted hover:bg-accent')
                  }
                  title={m.gpu ? 'GPU-only — requires ft.risk.bridge' : 'Local CPU'}
                >
                  {m.id}{m.gpu && ' · GPU'}
                </button>
              )
            })}
          </div>
        </div>

        {/* Run button */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            type="button"
            disabled={!fundId || models.length === 0 || horizons.length === 0 || confs.length === 0 || run.isPending}
            onClick={() => run.mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run VaR
          </button>
          {run.data && (
            <div className="text-sm text-muted-foreground">
              run_id <span className="font-mono">{run.data.run_id}</span> · n_obs {run.data.n_obs}
              {run.data.via_bridge && <> · via bridge ({run.data.bridge_runtime_ms}ms)</>}
            </div>
          )}
          {run.isError && (
            <div className="text-sm text-destructive">error — {String((run.error as any)?.response?.data?.detail ?? run.error)}</div>
          )}
        </div>
      </div>

      {/* Results matrix */}
      {matrix && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">VaR / ES across models</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Model</th>
                  {colKeys.map(({ key, h, c }) => (
                    <th key={key} className="text-right py-2 px-3 whitespace-nowrap">
                      <div className="text-xs text-muted-foreground uppercase">{h}d · {Math.round(c * 100)}%</div>
                      <div className="text-xs text-muted-foreground">VaR / ES</div>
                    </th>
                  ))}
                  <th className="text-left py-2 px-3">Method</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(matrix.entries()).map(([model, cells]) => (
                  <tr key={model} className="border-b border-border/50">
                    <td className="py-2 px-3 font-mono text-xs">{model}</td>
                    {colKeys.map(({ key }) => {
                      const r = cells[key]
                      return (
                        <td key={key} className="py-2 px-3 text-right whitespace-nowrap">
                          {r ? (
                            <div>
                              <div className="font-mono text-xs">{pct(r.var)}</div>
                              <div className="font-mono text-xs text-muted-foreground">{pct(r.es)}</div>
                            </div>
                          ) : '—'}
                        </td>
                      )
                    })}
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {Object.values(cells)[0]?.method ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-muted-foreground mt-3">
            VaR is reported as a positive loss magnitude. ES is the expected loss beyond VaR.
          </div>
        </div>
      )}
    </div>
  )
}
